import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { moviesApi, type MoviesUnmatchedFile } from "@/api/movies";
import { Button } from "@/components/ui/Button";
import { Field, Select, TextInput } from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { DownloadsEnabledCard } from "@/components/DownloadsEnabledCard";
import { PageHeader } from "@/components/ui/PageHeader";
import { Skeleton } from "@/components/ui/Skeleton";
import { Status } from "@/components/ui/Status";
import { useToast } from "@/components/ui/Toast";

const INTERVAL_PRESETS: { value: number; label: string }[] = [
  { value: 0, label: "Manual only" },
  { value: 1, label: "Every hour" },
  { value: 6, label: "Every 6 hours" },
  { value: 12, label: "Every 12 hours" },
  { value: 24, label: "Daily" },
  { value: 168, label: "Weekly" },
];

type MinAvail = "announced" | "in-cinemas" | "released" | "physical";

/**
 * Curated TMDB language presets — covers the most-requested locales
 * without forcing the user to type a BCP-47 tag from memory. The
 * input also accepts free-form values for niche tags TMDB supports
 * (`pt-BR`, `zh-TW`, etc) so this list is a convenience, not a gate.
 */
const LANGUAGE_PRESETS: { value: string; label: string }[] = [
  { value: "en-US", label: "English (US) — en-US" },
  { value: "en-GB", label: "English (GB) — en-GB" },
  { value: "fr-FR", label: "French — fr-FR" },
  { value: "de-DE", label: "German — de-DE" },
  { value: "es-ES", label: "Spanish (Spain) — es-ES" },
  { value: "es-MX", label: "Spanish (Mexico) — es-MX" },
  { value: "it-IT", label: "Italian — it-IT" },
  { value: "pt-BR", label: "Portuguese (Brazil) — pt-BR" },
  { value: "pt-PT", label: "Portuguese (Portugal) — pt-PT" },
  { value: "nl-NL", label: "Dutch — nl-NL" },
  { value: "sv-SE", label: "Swedish — sv-SE" },
  { value: "da-DK", label: "Danish — da-DK" },
  { value: "fi-FI", label: "Finnish — fi-FI" },
  { value: "nb-NO", label: "Norwegian (Bokmål) — nb-NO" },
  { value: "pl-PL", label: "Polish — pl-PL" },
  { value: "ru-RU", label: "Russian — ru-RU" },
  { value: "ja-JP", label: "Japanese — ja-JP" },
  { value: "ko-KR", label: "Korean — ko-KR" },
  { value: "zh-CN", label: "Chinese (Simplified) — zh-CN" },
  { value: "zh-TW", label: "Chinese (Traditional) — zh-TW" },
];

/** BCP-47 sanity check — same shape the API enforces. */
const BCP47_RE = /^[A-Za-z0-9_-]{2,35}$/;

export function MoviesSettings() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [libraryPath, setLibraryPath] = useState("");
  const [scanInterval, setScanInterval] = useState(0);
  const [refreshInterval, setRefreshInterval] = useState(0);
  const [minAvailability, setMinAvailability] = useState<MinAvail>("released");
  const [tmdbRegion, setTmdbRegion] = useState("US");
  const [metadataLanguage, setMetadataLanguage] = useState("en-US");
  const [altLanguages, setAltLanguages] = useState<string[]>([]);
  const [newAltLanguage, setNewAltLanguage] = useState("");
  // Migration 0062: alternate library root + private Jellyfin URL +
  // private-promoter source dir (the in-container path the background
  // task scans for orphan downloads from manual-search dispatches).
  const [privateLibraryPath, setPrivateLibraryPath] = useState("");
  const [privateJellyfinUrl, setPrivateJellyfinUrl] = useState("");
  const [privatePromoterSource, setPrivatePromoterSource] = useState("");

  const { data: settings, isLoading } = useQuery({
    queryKey: ["settings", "movies"],
    queryFn: moviesApi.settings.get,
  });
  const { data: status } = useQuery({
    queryKey: ["movies", "scan", "status"],
    queryFn: moviesApi.scan.status,
    refetchInterval: (q) => (q.state.data?.running ? 3000 : 30000),
  });

  useEffect(() => {
    if (settings) {
      setLibraryPath(settings.library_path ?? "");
      setScanInterval(settings.scan_interval_hours);
      setRefreshInterval(settings.refresh_interval_hours);
      setMinAvailability(settings.minimum_availability);
      setTmdbRegion(settings.tmdb_region);
      setMetadataLanguage(settings.metadata_language);
      setAltLanguages(settings.alt_metadata_languages);
      setPrivateLibraryPath(settings.private_library_path ?? "");
      setPrivateJellyfinUrl(settings.private_jellyfin_url ?? "");
      setPrivatePromoterSource(settings.private_promoter_source_dir ?? "");
    }
  }, [
    settings?.library_path,
    settings?.scan_interval_hours,
    settings?.refresh_interval_hours,
    settings?.minimum_availability,
    settings?.tmdb_region,
    settings?.metadata_language,
    settings?.alt_metadata_languages,
    settings?.private_library_path,
    settings?.private_jellyfin_url,
    settings?.private_promoter_source_dir,
  ]);

  const saveMut = useMutation({
    mutationFn: (body: {
      library_path?: string;
      scan_interval_hours?: number;
      refresh_interval_hours?: number;
      minimum_availability?: MinAvail;
      tmdb_region?: string;
      metadata_language?: string;
      alt_metadata_languages?: string[];
      private_library_path?: string;
      private_jellyfin_url?: string;
      private_promoter_source_dir?: string;
    }) => moviesApi.settings.update(body),
    onSuccess: () => {
      toast("Movies settings saved", "success");
      qc.invalidateQueries({ queryKey: ["settings", "movies"] });
      qc.invalidateQueries({ queryKey: ["movies", "scan", "status"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Save failed", "error"),
  });

  const scanMut = useMutation({
    mutationFn: () => moviesApi.scan.start(),
    onSuccess: () => {
      toast("Scan started", "info");
      qc.invalidateQueries({ queryKey: ["movies", "scan", "status"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Scan failed to start", "error"),
  });

  const onSave = (e: FormEvent) => {
    e.preventDefault();
    const region = tmdbRegion.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(region)) {
      toast("TMDB region must be a 2-letter ISO code (e.g. US, GB, DE)", "error");
      return;
    }
    const lang = metadataLanguage.trim();
    if (!BCP47_RE.test(lang)) {
      toast(
        "Primary metadata language must look like a BCP-47 tag (e.g. en-US, fr-FR)",
        "error",
      );
      return;
    }
    // Migration 0062: belt-and-suspenders shape check on the private
    // Jellyfin URL. Empty is fine (clears the setting); a non-empty
    // value must look URL-ish or the backend will 400 anyway.
    const jellyfin = privateJellyfinUrl.trim();
    if (jellyfin && !/^https?:\/\//i.test(jellyfin)) {
      toast(
        "Private Jellyfin URL must start with http:// or https://",
        "error",
      );
      return;
    }
    saveMut.mutate({
      library_path: libraryPath.trim(),
      scan_interval_hours: scanInterval,
      refresh_interval_hours: refreshInterval,
      minimum_availability: minAvailability,
      tmdb_region: region,
      metadata_language: lang,
      alt_metadata_languages: altLanguages,
      private_library_path: privateLibraryPath.trim(),
      private_jellyfin_url: jellyfin,
      private_promoter_source_dir: privatePromoterSource.trim(),
    });
  };

  const addAltLanguage = (raw: string) => {
    const tag = raw.trim();
    if (!tag) return;
    if (!BCP47_RE.test(tag)) {
      toast("Alt language must look like a BCP-47 tag (e.g. de-DE)", "error");
      return;
    }
    if (tag.toLowerCase() === metadataLanguage.toLowerCase()) {
      toast("That's already the primary language", "info");
      return;
    }
    if (altLanguages.some((l) => l.toLowerCase() === tag.toLowerCase())) {
      return;
    }
    setAltLanguages((prev) => [...prev, tag]);
    setNewAltLanguage("");
  };

  const removeAltLanguage = (tag: string) => {
    setAltLanguages((prev) => prev.filter((l) => l !== tag));
  };

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Settings"
        title="Movies"
        description="Library path, scan schedule, and TMDB metadata refresh schedule. The scanner walks the library and links existing files to monitored movies; the refresh task keeps TMDB metadata current."
      />

      <DownloadsEnabledCard kind="movies" />

      <ScanStatusCard
        loading={!status}
        running={status?.running ?? false}
        lastScanAt={status?.last_scan_at ?? null}
        unmatchedCount={status?.unmatched_count ?? 0}
        onScanNow={() => scanMut.mutate()}
        scanning={scanMut.isPending}
      />

      <form onSubmit={onSave} className="space-y-5">
        <Field
          label="Library path"
          hint={
            settings?.library_path_source === "env"
              ? "Currently sourced from MOVIES_LIBRARY_PATH env. Set a value here to override."
              : "Absolute path arr-in-one imports movies into and scans for existing files."
          }
        >
          <TextInput
            value={libraryPath}
            placeholder="/movies"
            onChange={(e) => setLibraryPath(e.target.value)}
          />
        </Field>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <Field
            label="Scan interval"
            hint="How often the background scanner walks the library and links existing files."
          >
            <Select
              value={String(scanInterval)}
              onChange={(e) => setScanInterval(Number(e.target.value))}
            >
              {INTERVAL_PRESETS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
              {INTERVAL_PRESETS.every((p) => p.value !== scanInterval) && (
                <option value={scanInterval}>
                  Every {scanInterval} hours (custom)
                </option>
              )}
            </Select>
          </Field>

          <Field
            label="TMDB refresh interval"
            hint="How often we re-fetch metadata (release dates, posters, IMDB ids) for monitored movies."
          >
            <Select
              value={String(refreshInterval)}
              onChange={(e) => setRefreshInterval(Number(e.target.value))}
            >
              {INTERVAL_PRESETS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
              {INTERVAL_PRESETS.every((p) => p.value !== refreshInterval) && (
                <option value={refreshInterval}>
                  Every {refreshInterval} hours (custom)
                </option>
              )}
            </Select>
          </Field>
        </div>

        {settings?.last_refresh_at && (
          <p className="font-mono text-[11px] text-foreground/45">
            Last metadata refresh{" "}
            <span className="text-foreground/65">
              {new Date(settings.last_refresh_at).toLocaleString()}
            </span>
          </p>
        )}

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <Field
            label="Minimum availability"
            hint="Auto-grab won't fire until the chosen release-date column is in the past. Per-movie override on the movie detail page."
          >
            <Select
              value={minAvailability}
              onChange={(e) => setMinAvailability(e.target.value as MinAvail)}
            >
              <option value="announced">Announced (no gate)</option>
              <option value="in-cinemas">In cinemas</option>
              <option value="released">Released (digital — recommended)</option>
              <option value="physical">Physical</option>
            </Select>
          </Field>

          <Field
            label="TMDB region"
            hint="Two-letter ISO 3166-1 country code TMDB filters release dates against. Defaults to US."
          >
            <TextInput
              value={tmdbRegion}
              maxLength={2}
              placeholder="US"
              onChange={(e) =>
                setTmdbRegion(e.target.value.toUpperCase().slice(0, 2))
              }
            />
          </Field>
        </div>

        <div className="space-y-4 rounded-md border border-line bg-surface/30 px-5 py-4">
          <div>
            <h3 className="eyebrow">Metadata languages</h3>
            <p className="mt-1 text-xs leading-relaxed text-foreground/55">
              The primary language is baked into each movie row (title,
              overview). Alt languages are fetched alongside on every add /
              refresh and surfaced as a Translations section on the movie
              detail page.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <Field
              label="Primary metadata language"
              hint="BCP-47 tag (e.g. en-US, fr-FR). Threaded through every TMDB call."
            >
              <Select
                value={
                  LANGUAGE_PRESETS.some((p) => p.value === metadataLanguage)
                    ? metadataLanguage
                    : "__custom__"
                }
                onChange={(e) => {
                  if (e.target.value !== "__custom__") {
                    setMetadataLanguage(e.target.value);
                  }
                }}
              >
                {LANGUAGE_PRESETS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
                {!LANGUAGE_PRESETS.some(
                  (p) => p.value === metadataLanguage,
                ) && (
                  <option value="__custom__">
                    {metadataLanguage} (custom)
                  </option>
                )}
              </Select>
              <TextInput
                className="mt-2"
                value={metadataLanguage}
                placeholder="en-US"
                onChange={(e) => setMetadataLanguage(e.target.value)}
              />
            </Field>

            <Field
              label="Add alt language"
              hint="Pick from the list or type a BCP-47 tag and press Add."
            >
              <div className="flex gap-2">
                <Select
                  value={newAltLanguage}
                  onChange={(e) => setNewAltLanguage(e.target.value)}
                  className="flex-1"
                >
                  <option value="">— pick a language —</option>
                  {LANGUAGE_PRESETS.filter(
                    (p) =>
                      p.value.toLowerCase() !==
                        metadataLanguage.toLowerCase() &&
                      !altLanguages.some(
                        (l) => l.toLowerCase() === p.value.toLowerCase(),
                      ),
                  ).map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </Select>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => addAltLanguage(newAltLanguage)}
                  disabled={newAltLanguage === ""}
                >
                  Add
                </Button>
              </div>
              <div className="mt-2 flex gap-2">
                <TextInput
                  value={newAltLanguage}
                  placeholder="de-DE"
                  onChange={(e) => setNewAltLanguage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addAltLanguage(newAltLanguage);
                    }
                  }}
                />
              </div>
            </Field>
          </div>

          {altLanguages.length > 0 && (
            <div>
              <div className="text-xs text-foreground/55 mb-1.5">
                Alt languages ({altLanguages.length})
              </div>
              <div className="flex flex-wrap gap-1.5">
                {altLanguages.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 rounded-full border border-line bg-surface/40 px-2.5 py-0.5 font-mono text-[11px] text-foreground/80"
                  >
                    {tag}
                    <button
                      type="button"
                      aria-label={`Remove ${tag}`}
                      onClick={() => removeAltLanguage(tag)}
                      className="text-foreground/45 hover:text-foreground/85"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
          {altLanguages.length === 0 && (
            <p className="text-xs text-foreground/45">
              No alt languages configured — translations side-table stays
              empty.
            </p>
          )}
        </div>

        {/* Migration 0062: private library + Watch deep-link target.
            Grouped together because they're meaningless without each
            other in the common case (the segregation lives at the
            filesystem layer; the Watch button only makes sense when
            there's actually a player pointing at that root). */}
        <div className="space-y-4 rounded-md border border-line bg-surface/30 p-4">
          <div>
            <h2 className="font-display text-sm font-medium tracking-display-tight">
              Private library
            </h2>
            <p className="mt-1 text-xs leading-relaxed text-foreground/60">
              Hide a subset of titles from the household media server by
              importing them to a different filesystem root. Plex /
              Jellyfin's container isn't mounted at this path, so it
              never sees the files. The Private tab on the Movies page
              is the only UI surface that lists flagged rows.
            </p>
          </div>
          <Field
            label="Private library path"
            hint="Absolute path the importer hardlinks private movies to. Leave blank to disable routing — the flag still hides rows from the default list, but the household server will still see the files at the public path."
          >
            <TextInput
              value={privateLibraryPath}
              placeholder="/other"
              onChange={(e) => setPrivateLibraryPath(e.target.value)}
            />
          </Field>
          <Field
            label="Private Jellyfin URL"
            hint="Base URL of a separate Jellyfin instance pointed exclusively at the private library root. Drives the Watch button on private movie detail pages. Leave blank to hide the button."
          >
            <TextInput
              value={privateJellyfinUrl}
              placeholder="https://private-jellyfin.example.com"
              onChange={(e) => setPrivateJellyfinUrl(e.target.value)}
              type="url"
            />
          </Field>
          <Field
            label="Promoter source directory"
            hint='In-container path the background "private promoter" task scans for orphan downloads from manual-search dispatches (releases NZBGet completed under the Private category). Found videos get hardlinked into the private library. Leave blank to use the default `/downloads/completed/private`.'
          >
            <TextInput
              value={privatePromoterSource}
              placeholder="/downloads/completed/private"
              onChange={(e) => setPrivatePromoterSource(e.target.value)}
            />
          </Field>
        </div>

        <div className="flex justify-end border-t border-line pt-4">
          <Button type="submit" loading={saveMut.isPending} disabled={isLoading}>
            Save changes
          </Button>
        </div>
      </form>

      <UnmatchedSection count={status?.unmatched_count ?? 0} />
    </div>
  );
}

function ScanStatusCard({
  loading,
  running,
  lastScanAt,
  unmatchedCount,
  onScanNow,
  scanning,
}: {
  loading: boolean;
  running: boolean;
  lastScanAt: string | null;
  unmatchedCount: number;
  onScanNow: () => void;
  scanning: boolean;
}) {
  return (
    <div className="rounded-md border border-line bg-surface/50 px-5 py-4">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1.5">
          <div className="eyebrow">Scanner</div>
          <div className="text-sm">
            {loading ? (
              <Skeleton.Block height="1rem" width="14rem" />
            ) : running ? (
              <Status tone="info" pulse>
                Scan running
              </Status>
            ) : lastScanAt ? (
              <span className="text-foreground/85">
                Last scan{" "}
                <span className="font-mono text-foreground/55">
                  {new Date(lastScanAt).toLocaleString()}
                </span>
              </span>
            ) : (
              <span className="text-foreground/55">
                Never scanned. Configure the library path below and run a scan.
              </span>
            )}
          </div>
          {unmatchedCount > 0 && (
            <p className="font-mono text-[11px] text-foreground/55">
              {unmatchedCount} unmatched file
              {unmatchedCount === 1 ? "" : "s"} from the last scan.
            </p>
          )}
        </div>
        <Button
          type="button"
          onClick={onScanNow}
          loading={scanning || running}
          disabled={running}
        >
          Scan now
        </Button>
      </div>
    </div>
  );
}

function UnmatchedSection({ count }: { count: number }) {
  const [expanded, setExpanded] = useState(false);
  const [claiming, setClaiming] = useState<MoviesUnmatchedFile | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ["movies", "scan", "unmatched"],
    queryFn: () => moviesApi.scan.unmatched(200),
    enabled: expanded && count > 0,
  });

  if (count === 0) return null;

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="eyebrow">Unmatched files</h2>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Hide" : "Show"}
        </Button>
      </div>
      <p className="text-xs leading-relaxed text-foreground/55">
        Files the scanner couldn't link to a monitored movie. Click Identify to
        assign one manually, or check that the filename includes "Title (Year)"
        for the auto-matcher.
      </p>
      {expanded &&
        (isLoading ? (
          <Skeleton.Rows count={4} />
        ) : !data || data.length === 0 ? (
          <p className="text-xs text-foreground/55">None.</p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-line bg-surface/30">
            <table className="w-full text-xs">
              <thead className="bg-surface-2/50">
                <tr className="text-left">
                  <th className="px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                    Path
                  </th>
                  <th className="px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                    Reason
                  </th>
                  <th className="w-24 px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line/60">
                {data.map((u) => (
                  <tr
                    key={u.id}
                    className="transition hover:bg-surface-2/40"
                  >
                    <td
                      className="max-w-md truncate px-3 py-2 font-mono text-[11px]"
                      title={u.path}
                    >
                      {u.path}
                    </td>
                    <td className="px-3 py-2 text-foreground/65">{u.reason}</td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setClaiming(u)}
                      >
                        Identify
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      {claiming && (
        <ClaimModal file={claiming} onClose={() => setClaiming(null)} />
      )}
    </section>
  );
}

function ClaimModal({
  file,
  onClose,
}: {
  file: MoviesUnmatchedFile;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [movieId, setMovieId] = useState<number | "">("");

  const movies = useQuery({
    queryKey: ["movies"],
    queryFn: () => moviesApi.list(),
  });

  const sortedMovies = useMemo(
    () =>
      (movies.data ?? [])
        .slice()
        .sort((a, b) => a.sort_title.localeCompare(b.sort_title)),
    [movies.data],
  );

  const claimMut = useMutation({
    mutationFn: () => {
      if (typeof movieId !== "number") {
        return Promise.reject(new Error("pick a movie"));
      }
      return moviesApi.scan.claim(file.id, { movie_id: movieId });
    },
    onSuccess: () => {
      toast("File claimed", "success");
      qc.invalidateQueries({ queryKey: ["movies", "scan"] });
      qc.invalidateQueries({ queryKey: ["movies"] });
      onClose();
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Claim failed", "error"),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    claimMut.mutate();
  };

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow="Manual import"
      title="Identify file"
      width="lg"
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="rounded-md border border-line bg-surface/40 px-3 py-2 text-xs">
          <div className="text-foreground/45">File</div>
          <div className="mt-0.5 truncate font-mono" title={file.path}>
            {file.path}
          </div>
        </div>

        <Field label="Movie">
          <Select
            value={movieId === "" ? "" : String(movieId)}
            onChange={(e) =>
              setMovieId(e.target.value === "" ? "" : Number(e.target.value))
            }
            disabled={movies.isLoading}
          >
            <option value="">— pick a movie —</option>
            {sortedMovies.map((m) => (
              <option key={m.id} value={m.id}>
                {m.title}
                {m.year ? ` (${m.year})` : ""}
              </option>
            ))}
          </Select>
        </Field>

        <div className="flex justify-end gap-2 border-t border-line pt-3">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={claimMut.isPending}
            disabled={typeof movieId !== "number"}
          >
            Claim
          </Button>
        </div>
      </form>
    </Modal>
  );
}
